// Angular modules.
import { Injectable } from '@angular/core';
// Third-party modules.
import { TranslateService } from '@ngx-translate/core';
import { IPeriod } from 'app/interfaces/date.interface';
// Zenklub interfaces.
import { ITimeZone, ITimeZoneList } from 'app/interfaces/timezone.interface';
import { IUserPreferences } from 'app/interfaces/userpreferences.interface';
import { DateTime } from 'luxon';
import * as moment from 'moment-timezone';

import { IPeriodOption } from './../interfaces/date.interface';
// Zenklub services.
import { GlobalService } from './global.service';
import { UserService } from './user.service';

interface Months {
	ABB: string;
	FULL: string;
}

interface Weekdays extends Months {
	INDEX: number;
	FIRST: string;
	SHORT: string;
}

@Injectable()
export class DateTimeService {
	private zones: string[] = [
		'Africa',
		'America',
		'Antarctica',
		'Artic',
		'Asia',
		'Atlantic',
		'Australia',
		'Europe',
		'Indian',
		'Pacific',
	];

	// User's preferences.
	public userPrefs: IUserPreferences;

	// Constructor method.
	constructor(
		protected global: GlobalService,
		protected translate: TranslateService,
		protected userService: UserService
	) {}

	// Get available timezones.
	getTimezones(): ITimeZoneList[] {
		if (!localStorage.getItem('zktimezones')) {
			const values: ITimeZoneList[] = [];
			const tzzones: string[] = moment.tz.names();

			for (const tzname of tzzones) {
				const val = tzname.split('/');
				let region: string;

				if (this.zones.indexOf(val[0]) !== -1) {
					if (val.length > 2) {
						const index = val.length - 1;
						const p2: string = val[index].replace(/_/gi, ' ');
						const p1: string = val[index - 1].replace(/_/gi, ' ');
						region = `${p1} - ${p2}`;
					} else {
						const index = val.length - 1;
						region = val[index] ? val[index].replace(/_/gi, ' ') : val[0];
					}
					const tz: ITimeZoneList = {
						zone: val[0],
						timeZoneId: tzname,
						region: region,
					};
					values.push(tz);
				}
			}
			localStorage.setItem('zktimezones', JSON.stringify(values));
			return values;
		}
		const tzones: any = localStorage.getItem('zktimezones');
		const ret: ITimeZoneList[] = JSON.parse(tzones);
		return ret;
	}

	// Get hours with ##:## format.
	public getFormatedHours(date: Date): string {
		date = this.toDate(date);
		const hours: number = date.getHours();
		const minutes: number = date.getMinutes();
		const finalHours = ('0' + hours).slice(-2);
		const finalMinutes = ('0' + minutes).slice(-2);

		return finalHours + ':' + finalMinutes;
	}

	// Get hours with ##:## format from isolated values.
	public getFormatedHoursAlt(hours: number, minutes = 0): string {
		const h: string = hours < 10 ? '0' + hours.toString() : hours.toString();
		const m: string =
			minutes < 10 ? '0' + minutes.toString() : minutes.toString();
		return h + ':' + m;
	}

	// Get a date string with slashes instead other separator.
	public getDateWithSlashes(date: string): string {
		return date ? date.replace(new RegExp('-', 'g'), '/') : '';
	}

	// Get month name based on his index.
	public getMonthName(monthIndex: number): Months {
		let monthName: string;

		switch (monthIndex) {
			case 0:
				monthName = 'JAN';
				break;
			case 1:
				monthName = 'FEB';
				break;
			case 2:
				monthName = 'MAR';
				break;
			case 3:
				monthName = 'APR';
				break;
			case 4:
				monthName = 'MAY';
				break;
			case 5:
				monthName = 'JUN';
				break;
			case 6:
				monthName = 'JUL';
				break;
			case 7:
				monthName = 'AUG';
				break;
			case 8:
				monthName = 'SEP';
				break;
			case 9:
				monthName = 'OCT';
				break;
			case 10:
				monthName = 'NOV';
				break;
			case 11:
				monthName = 'DEC';
				break;
		}

		return this.translate.instant('CALENDAR.MONTHS.' + monthName) as Months;
	}

	// Get weekday name based on his index (async).
	public getWeekdayNameAsync(dayIndex: number): string {
		const arDays: string[] = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
		return arDays[dayIndex];
	}

	// Get weekday name based on his index.
	public getWeekdayName(dayIndex: number): Weekdays {
		let dayName: string;

		switch (dayIndex) {
			case 0:
				dayName = 'SUN';
				break;
			case 1:
				dayName = 'MON';
				break;
			case 2:
				dayName = 'TUE';
				break;
			case 3:
				dayName = 'WED';
				break;
			case 4:
				dayName = 'THU';
				break;
			case 5:
				dayName = 'FRI';
				break;
			case 6:
				dayName = 'SAT';
				break;
		}
		return this.translate.instant('CALENDAR.WEEKDAYS.' + dayName) as Weekdays;
	}

	// Verify if a given date is today.
	public isToday(date: Date): boolean {
		// Get today.
		date = this.toDate(date);
		const today: Date = new Date();
		return date.toDateString() === today.toDateString();
	}

	public daysPassedFrom(date: Date): number {
		return moment().diff(date, 'days');
	}

	public isYesterday(date: Date): boolean {
		return moment().subtract(1, 'days').isSame(date, 'date');
	}

	public isSameMonth(date: Date): boolean {
		return moment().isSame(date, 'month') && moment().isSame(date, 'year');
	}

	public isSameYear(date: Date): boolean {
		return moment().isSame(date, 'year');
	}

	// Verify if a given date is between t2o dates interval.
	public isInRange(date: Date, min: Date, max: Date): boolean {
		date = this.toDate(date);
		// Reset time.
		date.setHours(0, 0, 0, 0);
		min.setHours(0, 0, 0, 0);
		max.setHours(0, 0, 0, 0);

		// Get values.
		const cDate: number = Date.UTC(
			date.getFullYear(),
			date.getMonth(),
			date.getDate()
		);
		const cMin: number = Date.UTC(
			min.getFullYear(),
			min.getMonth(),
			min.getDate()
		);
		const cMax: number = Date.UTC(
			max.getFullYear(),
			max.getMonth(),
			max.getDate()
		);

		// Return result.
		return cDate >= cMin && cDate <= cMax;
	}

	// Verify if a given date is before a deadline
	public dateIsBefore(
		date: Date | string,
		deadline: Date | string = new Date()
	): boolean {
		date = this.toDate(date);
		deadline = this.toDate(deadline);

		// Reset time.
		date.setHours(0, 0, 0, 0);
		deadline.setHours(0, 0, 0, 0);

		// Get values.
		const cDate: number = Date.UTC(
			date.getFullYear(),
			date.getMonth(),
			date.getDate()
		);
		const cDeadline: number = Date.UTC(
			deadline.getFullYear(),
			deadline.getMonth(),
			deadline.getDate()
		);

		// Return result.
		return cDate >= cDeadline;
	}

	// Verify if a given date is after a deadline
	public dateIsAfter(
		date: Date | string,
		deadline: Date | string = new Date()
	): boolean {
		date = this.toDate(date);
		deadline = this.toDate(deadline);

		// Reset time.
		deadline.setHours(0, 0, 0, 0);

		// Get values.
		const cDate: number = Date.UTC(
			date.getFullYear(),
			date.getMonth(),
			date.getDate()
		);
		const cDeadline: number = Date.UTC(
			deadline.getFullYear(),
			deadline.getMonth(),
			deadline.getDate()
		);

		// Return result.
		return cDate <= cDeadline;
	}

	// Verify if a given date and time is before a deadline
	public dateTimeIsBefore(
		date: Date | string | moment.Moment,
		deadline: Date | string | moment.Moment = moment()
	): boolean {
		return moment(date).isBefore(moment(deadline));
	}

	// Verify if a given date and time is after a deadline
	public dateTimeIsAfter(
		date: Date | string | moment.Moment,
		deadline: Date | string | moment.Moment = moment()
	): boolean {
		return moment(date).isAfter(moment(deadline));
	}

	// Get hour interval.
	public getHourInterval(date?: Date): string {
		date = this.toDate(date);
		const hour: number = date.getHours();
		return hour.toString().padStart(2, '0') + ':00';
	}

	// Build daily hours interval.
	public buildDailyHours(useHalfHour = false): string[] {
		if (useHalfHour) {
			return [
				'00:00',
				'00:30',
				'01:00',
				'01:30',
				'02:00',
				'02:30',
				'03:00',
				'03:30',
				'04:00',
				'04:30',
				'05:00',
				'05:30',
				'06:00',
				'06:30',
				'07:00',
				'07:30',
				'08:00',
				'08:30',
				'09:00',
				'09:30',
				'10:00',
				'10:30',
				'11:00',
				'11:30',
				'12:00',
				'12:30',
				'13:00',
				'13:30',
				'14:00',
				'14:30',
				'15:00',
				'15:30',
				'16:00',
				'16:30',
				'17:00',
				'17:30',
				'18:00',
				'18:30',
				'19:00',
				'19:30',
				'20:00',
				'20:30',
				'21:00',
				'21:30',
				'22:00',
				'22:30',
				'23:00',
				'23:30',
				'00:00',
			];
		}
		return [
			'00:00',
			'01:00',
			'02:00',
			'03:00',
			'04:00',
			'05:00',
			'06:00',
			'07:00',
			'08:00',
			'09:00',
			'10:00',
			'11:00',
			'12:00',
			'13:00',
			'14:00',
			'15:00',
			'16:00',
			'17:00',
			'18:00',
			'19:00',
			'20:00',
			'21:00',
			'22:00',
			'23:00',
			'00:00',
		];
	}

	// Get date in a simple format.
	public getSimpleDate(date: Date, separator = '/'): string {
		const formattedDate = date
			.toISOString()
			.substring(0, 10)
			.replace(/-/g, separator);

		return formattedDate;
	}

	public getShortDate(isoDate: string, timezoneName: string): string {
		const userLanguage = this.userService.getUserLocalPreferences().Language;

		return DateTime.fromISO(isoDate, {
			zone: timezoneName,
		}).toLocaleString(DateTime.DATE_SHORT, {
			locale: userLanguage,
		});
	}

	public toISOString(
		date: Date,
		datePeriod: 'startOfDay' | 'endOfDay',
		timezoneName?: string
	) {
		let userDate = this.setUTZTimezone(date, timezoneName);

		if (datePeriod === 'startOfDay') {
			userDate = userDate.startOf('day');
		}

		if (datePeriod === 'endOfDay') {
			userDate = userDate.endOf('day');
		}

		return userDate.toUTC().toISO();
	}

	public setUTZTimezone(date: Date, timezoneName?: string): DateTime {
		return DateTime.fromJSDate(date, {
			zone: timezoneName || this.userService.getTimezone(),
		});
	}

	// Get day of week index from a date.
	public getDayOfWeekOfDate(date: any, ignoreOffset = false): number {
		date = this.toDate(date);
		if (ignoreOffset) {
			return moment.parseZone(date).weekday();
		}
		return new Date(date).getDay();
	}

	// Get an hour in string format ("08:00").
	public getHourString(h: number, m: number): string {
		const newH: string = h < 10 ? '0' + h : h.toString();
		const newM: string = m < 10 ? '0' + m : m.toString();
		return newH + ':' + newM;
	}

	// Get an hour in string format ignoring seconds.
	// e.i., receive 15:30:45 and returns 15:30.
	public getHourAndMinutes(hour: string): string {
		const hm: string[] = hour.split(':');
		return hm[0] + ':' + hm[1];
	}

	// Get full hour (minutes are ignored).
	// e.i., receive 15:32 and returns 15:00.
	public getFullHour(date?: Date): string {
		date = this.toDate(date);
		const hour: number = date.getHours();
		return (hour < 10 ? '0' + hour : hour).toString() + ':00';
	}

	// Get date in unabbreviated mode.
	// e.i., receive 2017-11-23 and returns november 23, 2017.
	public getInFullDate(date?: Date, showDayName = false): string {
		date = this.toDate(date);
		// Get weekday name.
		const wName = this.getWeekdayName(date.getDay());
		const mon = this.getMonthName(date.getMonth());
		// Store day name.
		const dayName: string = wName.ABB;

		// Response holder.
		let response: string;
		// Get user's preferences.
		this.userPrefs = this.userService.getUserLocalPreferences();
		// Get active language.
		const lang: string = this.userPrefs.Language;
		// Date info.
		const day: number = date.getDate();
		const month: string = mon.FULL;
		const year: number = date.getFullYear();

		// Build response.
		switch (lang.toLowerCase()) {
			case 'en-us':
				response = month + ' ' + day + ', ' + year;
				break;
			default:
				const showDay = day.toString();
				response = showDay + ' de ' + month + ' de ' + year;
				break;
		}
		if (showDayName) {
			response = dayName + ', ' + response;
		}

		return response;
	}

	getReadbleFormatDateLang(lang) {
		if (lang.toLowerCase() === 'en-us') {
			return `MMMM DD[,] YYYY`;
		} else {
			return `dddd[,] DD [de] MMMM [de] YYYY`;
		}
	}

	// Get a date from a string.
	public getDateFromString(date: string): Date {
		const lang: string = this.translate.currentLang;
		const separator: string = this.getDateSeparator(date);
		const splited: any = date.split(separator);
		let dt: Date;

		switch (lang) {
			case 'en-US':
				dt = new Date(splited[0], splited[1], splited[2]);
				break;
			default:
				dt = new Date(splited[2], splited[1], splited[0]);
		}
		return dt;
	}

	// Get a date according current language.
	public getTranslatedDate(date: Date, includeTime = false): string {
		const lang: string = this.translate.currentLang;
		date = this.toDate(date);
		const y: number = date.getFullYear();
		const m: number = date.getMonth() + 1;
		const d: number = date.getDate();

		const sM: string = m < 10 ? '0' + m : m.toString();
		const sD: string = d < 10 ? '0' + d : d.toString();
		let newDate = sD + '/' + sM + '/' + y;

		if (lang === 'en-US') {
			newDate = y + '/' + sM + '/' + sD;
		}
		if (includeTime) {
			newDate += ' - ' + this.getFormatedHours(date);
		}

		return newDate;
	}

	public getDateMask() {
		return 'dd/MM/yyyy';
	}

	// Validate an hour format.
	public validateHour(hour: string): boolean {
		if (hour.indexOf(':') === -1) {
			return false;
		}
		const splitShort: string[] = hour.split(':');
		const partH: number = parseInt(splitShort[0], 10);
		const partM: number = parseInt(splitShort[1], 10);
		return partH >= 0 && partH <= 23 && partM >= 0 && partM <= 59;
	}

	// Get a date in string format.
	public getStringDate(
		date: Date,
		separator = '/',
		ignoreOffset = false
	): string {
		date = this.toDate(date);
		const strDate: moment.Moment = ignoreOffset
			? moment.parseZone(date)
			: moment(date);
		const lang: string = this.translate.currentLang;

		// Get values.
		const y: string = strDate.year().toString();
		let m: string = (strDate.month() + 1).toString();
		let d: string = strDate.date().toString();

		// Ensure two digits.
		m = parseInt(m, 10) <= 9 ? '0' + m : m;
		d = parseInt(d, 10) <= 9 ? '0' + d : d;

		switch (lang) {
			case 'en-US':
				return y + separator + m + separator + d;
			default:
				return d + separator + m + separator + y;
		}
	}

	// Get a date in string format ignoring offset.
	public getStringDateWithoutOffset(date: Date, separator = '/'): string {
		date = this.toDate(date);
		const strDate: string = moment.parseZone(date).format('YYYY-MM-DD');
		const lang: string = this.translate.currentLang;
		let dt: string;

		// Get values.
		const y: string = moment(strDate).year().toString();
		let m: string = (moment(strDate).month() + 1).toString();
		let d: string = moment(strDate).date().toString();

		// Ensure two digits.
		m = parseInt(m, 10) <= 9 ? '0' + m : m;
		d = parseInt(d, 10) <= 9 ? '0' + d : d;

		switch (lang) {
			case 'en-US':
				dt = y + separator + m + separator + d;
				break;
			default:
				dt = d + separator + m + separator + y;
		}
		return dt;
	}

	// Get separator type of a date.
	public getDateSeparator(date: string): string {
		let separator: string;

		if (date.indexOf('-') > -1) {
			separator = '-';
		} else if (date.indexOf('/') > -1) {
			separator = '/';
		} else if (date.indexOf('.') > -1) {
			separator = '.';
		}
		return separator;
	}

	// TIME ZONE RELATED. =====================================================================================

	// Get time zone info (e.g., GMT-0300 (Hora oficial do Brasil)).
	public getTimezoneInfo(date?: Date): ITimeZone {
		date = this.toDate(date);
		const hoursDiff: string = this.getHoursToUTCAlt(date);

		let zoneName = moment.tz.guess();
		zoneName = zoneName
			? zoneName.substring(zoneName.indexOf('/') + 1).replace('_', ' ')
			: 'Não identificado';
		const localInfo = zoneName + ' (' + hoursDiff + ')';
		// Build time zone object.
		const tzInfo: ITimeZone = {
			name: this.getTimezoneName(),
			country: this.userService.getUserCountry(),
			hoursToUTC: hoursDiff,
			// minutesToUTC: this.getMinutesToUTC(date),
			minutesToUTC: this.getTimezoneOffset(date),
			zone: localInfo,
		};
		// Return info.
		return tzInfo;
	}

	// Get time zone name of user (e.g., America/Sao_Paulo).
	public getTimezoneName(): string {
		// Try to get from local storage.
		const zku = this.userService.getUserLocal();
		if (zku && zku.timezone && moment.tz.zone(zku.timezone)) {
			return zku.timezone;
		}
		// Return stored or get new.
		return moment.tz.guess();
	}

	// Get minutes to UTC (e.g., 180).
	public getMinutesToUTC(date?: Date): number {
		date = this.toDate(date);
		return -moment(date).toDate().getTimezoneOffset();
	}

	// Get hours to UTC (e.g., -03).
	public getHoursToUTC(date?: Date): string {
		date = this.toDate(date);
		// Get time stamp and zone name.
		const timeStamp: number = moment(date).toDate().getUTCHours();
		const timezoneName: string = this.getTimezoneName();
		// Return difference.
		return moment.tz.zone(timezoneName).abbr(timeStamp);
	}

	// Get hours to UTC (e.g., -02:00)
	public getHoursToUTCAlt(date?: Date): string {
		date = this.toDate(date);
		return moment(date).tz(this.getTimezoneName()).format('Z');
	}

	getIsoStringDateNoTime(value: Date | string): string {
		const date: Date =
			value instanceof Date ? value : new Date(value.replace(/[T\s].+/, ''));
		return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
			2,
			'0'
		)}-${String(date.getUTCDate()).padStart(2, '0')}T00:00:00.000Z`;
	}

	// Get text for show UTC info (i.e., UTC-1).
	getUTCDate(date: Date): string {
		date = this.toDate(date);
		const utc: number = date.getTimezoneOffset();
		const signal: string = (utc / 60) * -1 > 0 ? '+' : '-';
		return 'UTC' + signal + utc / 60;
	}

	// Get UTC info formated for screen.
	getFormatedUTC(date: Date): string {
		return date.getTimezoneOffset().toString();
	}

	// Get offset of timezone.
	getTimezoneOffset(date: Date = new Date()): number {
		const jsOffset: number = date.getTimezoneOffset();
		return jsOffset;
	}

	// Get local offset.
	public getLocalOffset(date: Date): Date {
		date = this.toDate(date);
		const localDateF: string = this.getDateWithSlashes(date.toString());
		const localDate = new Date(localDateF);
		const offset: number = new Date().getTimezoneOffset();
		localDate.setMinutes(localDate.getMinutes() - offset);
		return localDate;
	}

	// Get date and time based on a given UTC date.
	public getLocalDate(date: Date, minutesToUTC = 0): Date {
		// Convert date.
		const localDate: Date = this.toDate(date);
		// Get minutes do UTC related to the given date.
		// If '0' try to get local offset (may be '0' to).
		minutesToUTC =
			minutesToUTC !== 0 ? minutesToUTC : localDate.getTimezoneOffset();
		localDate.setTime(localDate.getTime() - minutesToUTC * 60 * 1000);
		// Return date with local offset.
		return localDate;
	}

	public getBrowserTimezone(): string {
		const currentTz = moment.tz.guess();
		const currentTzName = currentTz
			? currentTz.substring(currentTz.indexOf('/') + 1).replace('_', ' ')
			: 'Não identificado';
		const currentTzOffset = moment().tz(currentTz).format('Z');
		return `${currentTzName} (${currentTzOffset})`;
	}

	private toDate(date?: string | Date) {
		if (typeof date === 'string') {
			return new Date(date);
		}
		if (date && typeof date === 'object') {
			return date;
		}
		return new Date();
	}

	// add minutes to date
	addMinutes(minutes: number, date?: Date | string): Date {
		date = this.toDate(date);
		date.setMinutes(date.getMinutes() + minutes);
		return date;
	}

	// Get period options to filter by

	getPeriodOptions(returnOptions: 'all' | IPeriod[] = 'all'): IPeriodOption[] {
		const periodOptions: IPeriodOption[] = [];
		for (const value in Object.keys(IPeriod)) {
			if (typeof IPeriod[value] !== 'string') {
				continue;
			}
			periodOptions.push({
				label: this.translate.instant(
					`PERIODS.${IPeriod[value].toUpperCase()}`
				),
				value: IPeriod[value],
			});
		}

		return returnOptions === 'all'
			? periodOptions
			: periodOptions.filter((option) => {
					return returnOptions.includes(option.value);
			  });
	}
}
